import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { comTelefoneLegivel } from '../formato';
import { nomeDoEspaco, type EspacoDeTrabalho } from '../modulos';
import { CartaoDePendencia } from '../painel/CartaoDePendencia';
import { GRAVIDADE_DO_NIVEL } from '../painel/doRadar';
import type { Gravidade, Pendencia } from '../painel/tipos';
import { useSessao } from '../sessao';
import { degrauDe, NIVEL_DO_RADAR } from '../situacoes';
import { AtualizacaoPausada, useTempoReal } from '../tempoReal';
import { Aviso, Carregando, ErroDaApi, Vazio } from '../ui';
import '../painel/painel.css';
import './inicio.css';

/**
 * O Início do Hub — «O que precisa de você hoje» — onda 550 (Plano 2 ·
 * Comercial, Etapa 1, Tarefa 20; spec do Comercial §4.5). `/inicio`, a porta
 * do Hub.
 *
 * Os pendentes de todos os espaços que a pessoa alcança (`GET /hub/hoje`),
 * agrupados por espaço. A ordem vem da API — do mais grave ao mais leve e,
 * em cada nível, do mais antigo —, e os grupos seguem a mesma ordem: abre
 * primeiro o espaço que tem o mais grave.
 *
 * Cada item é um cartão da pilha do Painel (`CartaoDePendencia`, RN-256): o
 * que é, o que falta, o nível em palavra E em cor, e a ação — o botão no PC,
 * o cartão inteiro no celular. O nível fala a escada do Radar (Crítico, Em
 * risco, Em andamento), com a cor da pilha (trava, atenção, para saber). Um
 * registro (uma conversa, uma proposta) não tem número; uma situação (os
 * ensaios esperando a sua verificação), sim.
 *
 * A pilha do Painel (`PilhaDePendencias`) não entra inteira: o «Tudo |
 * Comigo» e os chips de área dela não têm o que filtrar aqui — tudo já é da
 * pessoa, e a área é o grupo.
 *
 * Ao vivo pela caixa e pelo funil, para quem tem o Comercial, e de minuto em
 * minuto, porque o nível anda com o relógio.
 */

type Nivel = 'critico' | 'em_risco' | 'em_andamento';

export interface ItemDoHoje {
  /** O registro ou a situação (`conversa:<id>`, `ensaio_verificar`): a chave do cartão. */
  chave: string;
  espaco: string;
  tipo: string;
  titulo: string;
  detalhe: string;
  nivel: Nivel;
  desde: string | null;
  link: string;
  quantos?: number;
  /** D7: a oportunidade e as pendências dela, quando a linha junta mais de uma. */
  oportunidadeId?: string | null;
  pendencias?: { tipo: string; titulo: string; detalhe: string; nivel: string; link: string }[];
}

/** A cor da pilha para cada nível do Radar: a mesma do «Hoje» do painel do Comercial (`painel/doRadar.ts`). */
const GRAVIDADE: Record<Nivel, Gravidade> = GRAVIDADE_DO_NIVEL as Record<Nivel, Gravidade>;

/** O que o item é, na etiqueta do cartão. */
const TIPO: Record<string, string> = {
  conversa: 'Conversa',
  conversa_passada: 'Conversa',
  proposta: 'Proposta',
  tarefa: 'Tarefa',
  ensaio_verificar: 'Ensaios',
  ensaio_aprovar: 'Ensaios',
};

const ACAO: Record<string, string> = {
  conversa: 'Abrir a conversa',
  conversa_passada: 'Abrir a conversa',
  proposta: 'Abrir a proposta',
  ensaio_verificar: 'Verificar',
  ensaio_aprovar: 'Aprovar',
};

const NADA = 'Nada esperando por você agora.';
/** A busca de novo falhou depois da primeira (M-5): a lista fica, mas avisada. */
const SEM_ATUALIZAR = 'Não foi possível atualizar o que precisa de você.';
const SEM_ATUALIZAR_COMPLEMENTO = 'A lista abaixo é a da última busca que deu certo. A tela tenta de novo a '
  + 'cada minuto; se o aviso continuar, confira a conexão ou recarregue a página.';
const NADA_COMPLEMENTO = 'Quando algo de um dos seus espaços depender de você, aparece aqui.';

function paraPendencia(i: ItemDoHoje): Pendencia {
  /* D7: a linha de uma oportunidade com várias pendências abre a oportunidade e diz as outras. */
  const varias = (i.pendencias?.length ?? 0) > 1;
  const acao = varias ? 'Abrir a oportunidade' : ACAO[i.tipo]
    ?? (i.link.startsWith('/comercial/oportunidade/') ? 'Abrir a oportunidade' : 'Abrir a conversa');
  return {
    chave: i.chave,
    numero: typeof i.quantos === 'number' ? i.quantos : null,
    titulo: comTelefoneLegivel(i.titulo),
    consequencia: i.detalhe,
    detalhe: varias
      ? `Também: ${i.pendencias!.slice(1).map((p) => `${comTelefoneLegivel(p.titulo)} — ${p.detalhe}`).join('; ')}.`
      : undefined,
    gravidade: GRAVIDADE[i.nivel] ?? 'saber',
    palavra: degrauDe(NIVEL_DO_RADAR, i.nivel).texto,
    area: TIPO[i.tipo] ?? 'Outro',
    acao: { texto: acao, para: i.link },
  };
}

/** Os grupos por espaço, na ordem em que o primeiro item de cada um aparece. */
function agrupar(itens: ItemDoHoje[]): { espaco: string; itens: ItemDoHoje[] }[] {
  const grupos: { espaco: string; itens: ItemDoHoje[] }[] = [];
  for (const i of itens) {
    const g = grupos.find((x) => x.espaco === i.espaco);
    if (g) g.itens.push(i); else grupos.push({ espaco: i.espaco, itens: [i] });
  }
  return grupos;
}

export function Inicio() {
  const { eu } = useSessao();
  const [itens, setItens] = useState<ItemDoHoje[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const pedido = useRef(0);

  const buscar = useCallback(async () => {
    const n = ++pedido.current;
    try {
      const r = await api<{ itens: ItemDoHoje[] }>('/hub/hoje');
      if (n !== pedido.current) return;
      setItens(r.itens);
      setErro(null);
    } catch (e) {
      if (n === pedido.current) setErro(e);
    }
  }, []);

  useEffect(() => { void buscar(); }, [buscar]);
  useEffect(() => {
    const id = window.setInterval(() => { void buscar(); }, 60_000);
    return () => window.clearInterval(id);
  }, [buscar]);

  const comercial = (eu?.espacos ?? []).includes('comercial');
  const adiada = useRef<number | null>(null);
  const tempoReal = useTempoReal(comercial ? ['comercial.caixa', 'comercial.funil'] : [], () => {
    if (adiada.current !== null) return;
    adiada.current = window.setTimeout(() => { adiada.current = null; void buscar(); }, 300);
  });
  useEffect(() => () => { if (adiada.current !== null) window.clearTimeout(adiada.current); }, []);

  return (
    <div className="inicio-tela">
      <header className="cabeca">
        <div className="cresce">
          <h1>O que precisa de você hoje</h1>
          <p className="sub">
            De cada espaço que você alcança, o mais grave primeiro e, no mesmo nível, o mais antigo.
          </p>
        </div>
      </header>
      <AtualizacaoPausada estado={tempoReal} />
      {!!erro && itens && (
        <div className="inicio-sem-atualizar">
          <Aviso tipo="erro" titulo={SEM_ATUALIZAR}>{SEM_ATUALIZAR_COMPLEMENTO}</Aviso>
        </div>
      )}

      {erro && !itens ? <ErroDaApi erro={erro} />
        : !itens ? <Carregando texto="Carregando o que precisa de você" />
          : itens.length === 0 ? (
            <div className="inicio-vazio">
              <Vazio>{NADA} {NADA_COMPLEMENTO}</Vazio>
            </div>
          ) : agrupar(itens).map((g) => {
            const idTitulo = `inicio-espaco-${g.espaco}`;
            return (
              <section key={g.espaco} className="inicio-espaco" data-espaco={g.espaco} aria-labelledby={idTitulo}>
                <h2 id={idTitulo} className="inicio-espaco-titulo">
                  {nomeDoEspaco(g.espaco as EspacoDeTrabalho)} <span className="inicio-conta">{g.itens.length}</span>
                </h2>
                <div className="pn-cartoes inicio-cartoes">
                  {g.itens.map((i) => {
                    const p = paraPendencia(i);
                    return (
                      <div key={p.chave} className="inicio-item" data-link={i.link} data-tipo={i.tipo}>
                        <CartaoDePendencia p={p} />
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
    </div>
  );
}
